import { Icon, Image, Tooltip, Typography } from '@smartgift/frontend-toolkit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { CardContent, LinearProgressBar } from '@hero/atoms';
import { DetailItem } from '@hero/molecules';
import { getAverageTimeFromSeconds } from '@hero/utils';
import { formatPercentage } from '@hero/utils/helper';

import { EmptySection } from './EmptySection';
import * as S from './style';

function GiftSelection({ gifts, analytics, isContactLess }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const totalGiftCount = useMemo(
    () => gifts?.reduce?.((acc, gift) => acc + gift.count, 0) || 0,
    [gifts]
  );
  const averageAcceptanceRate = useMemo(
    () => getAverageTimeFromSeconds(analytics?.avgAcceptanceTimeInSeconds),
    [analytics]
  );

  const giftList = useMemo(() => {
    return gifts
      ?.map(gift => {
        const percentage = formatPercentage((gift.count / totalGiftCount) * 100);

        return {
          ...gift,
          percentage
        };
      })
      .filter(gift => gift);
  }, [gifts]);

  return (
    <S.GiftSelection>
      <S.CardHeader>
        <Typography variant="body1Bold">{t('giftSelection')}</Typography>
      </S.CardHeader>
      <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <S.Card>
          {giftList.length > 0 ? (
            <S.ScrollArea>
              {giftList?.map?.(gift => {
                return (
                  <S.GiftItem key={gift.id}>
                    <S.ImageWrapper>
                      <Image
                        src={gift.images?.[0]?.url}
                        errorSrc={theme.images.noImage}
                        alt={gift?.name}
                      />
                      {gift?.type === 'charity' && (
                        <Tooltip
                          content={t('charityIconTooltipContent')}
                          tooltipConfig={{ placement: 'top', offset: [0, -20] }}
                        >
                          <S.IconWrapper>
                            <Icon name="f-heart" color="var(--color-error-default)" size={12} />
                          </S.IconWrapper>
                        </Tooltip>
                      )}
                    </S.ImageWrapper>
                    {gift?.name && (
                      <Typography style={{ width: 150 }} lineClamp={2}>
                        {gift.name}
                      </Typography>
                    )}

                    <S.BarArea>
                      <LinearProgressBar
                        backgroundColor="var(--color-warning-weaker)"
                        visualParts={[
                          {
                            percentage: gift?.percentage,
                            color: 'var(--color-warning-default)'
                          }
                        ]}
                      />
                      <S.Rate>
                        {gift?.percentage}({gift?.count})
                      </S.Rate>
                    </S.BarArea>
                  </S.GiftItem>
                );
              })}
            </S.ScrollArea>
          ) : (
            <EmptySection />
          )}
        </S.Card>

        {!isContactLess && (
          <S.CardFooter>
            <DetailItem title={`${t('avgAcceptanceTime')}:`} className="average-detail-item">
              <S.AvgWrapper>
                {averageAcceptanceRate ? (
                  <S.AvgTimeText>
                    <Icon name="tooltip" />
                    {averageAcceptanceRate}
                  </S.AvgTimeText>
                ) : (
                  <S.AvgTimeText>{t('notAcceptedYet')}</S.AvgTimeText>
                )}
              </S.AvgWrapper>
            </DetailItem>
          </S.CardFooter>
        )}
      </CardContent>
    </S.GiftSelection>
  );
}

export { GiftSelection };
